import React, { useCallback, useContext, useEffect, useState } from "react";
import styled from "styled-components";
import Offer from "../../../dtos/Offer";
import RestrictionMessage from "../../../common/components/RestrictionMessage";
import FamilyMember from "../../../dtos/FamilyMember";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { Column, Row } from "../../../common/styled-components/layout";
import {
  BoldParagraph,
  H1,
  H2,
  Paragraph,
  PrecisionParagraph,
  PreFormattedParagraph
} from "../../../common/styled-components/typography";
import {
  familyMembersToSelectOptions,
  restrictions,
} from "../../../utils/qidigo";
import InputSelect from "../../../common/components/forms/InputSelect";
import { getHtmlContent } from "../../../utils/text";
import InputSelectOption from "../../../common/components/forms/InputSelectOption";
import PrimaryButton from "../../../common/components/PrimaryButton";
import {
  OutlineButton,
  PrimaryLinkButton,
} from "../../../common/styled-components/buttons";
import { NavigationContext } from "../../../contexts/NavigationContextProvider";
import { AddToCartResponse } from "../../../common/requests";
import { ApplicationFlowContext } from "../../../contexts/ApplicationFlowContextProvider";
import { useDateFormats } from "../../../hooks/useDateFormats";
import {ConsentModal} from "../../../common/components/ConsentModal";
import {IRestrictions} from "../../wishlist/types";
import MoneyDisplay from "../../../common/components/MoneyDisplay";

const groupsAllHaveWaitingLists = (groups: { waiting_list: boolean }[]) =>
  !groups.find((group) => !group.waiting_list);

interface CompleteSessionOfferProps {
  offer: Offer;
  familyMembers: { [key: string]: Partial<FamilyMember> };
  addSessionToCart: (
    userId: number,
    planId: number,
    successMessage?: string
  ) => Promise<AddToCartResponse>;
  removeSessionFromCart: (itemId: number) => Promise<void>;
  shouldRenderMembershipOptions?: boolean;
  selectedMemberId?: string;
  userId: number;
  organizationId: number;
  hasContact: boolean;
}

const CompleteSessionOffer = ({
  offer,
  familyMembers,
  addSessionToCart,
  removeSessionFromCart,
  shouldRenderMembershipOptions = false,
  selectedMemberId,
  userId,
  organizationId,
  hasContact,
}: CompleteSessionOfferProps) => {
  const { t } = useTranslation();
  const { shoppingCartUrl } = useContext(NavigationContext);

  const membersOptions = familyMembersToSelectOptions(t, familyMembers);

  const [selectedMember, setSelectedMember] = useState(
    selectedMemberId
      ? membersOptions.find((x) => String(x.value) === String(selectedMemberId))
      : membersOptions[0]
  );
  const [selectedOption, setSelectedOption] = useState(
    offer.options.find((opt) => opt.subscriber === selectedMember?.value)
  );

  const [itemAddedToCart, setItemAddedToCart] = useState<number | null>(null);
  const { displaySuccessMessage } = useContext(ApplicationFlowContext);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [hasConsent, setHasConsent] = useState<boolean>(hasContact);
  const onConsent = () => {
    setIsModalOpen(false);
    setHasConsent(true);
  }

  const makeListOfRestrictionCodes = (restrictions: IRestrictions) => {
    let groupRestrictions: never[] = [];
    restrictions.groups_restrictions.map((groupRestriction) => groupRestriction.restrictions)
        .forEach((restrictions) => {
          groupRestrictions = groupRestrictions.concat(restrictions.filter((restriction: any) => {
            return groupRestrictions.find(
                (addedRestriction) => addedRestriction === restriction
            ) === undefined;
          }));
        });

    const productMembershipRestrictions = restrictions.product_restrictions.filter((restriction) => {
      return restriction.memberships !== undefined;
    })

    const productRestrictions =  restrictions.product_restrictions.filter((restriction) => {
      return restriction.memberships === undefined;
    }).map((restriction) => {return restriction.code});


    return ([] as (any | never)[]).concat(
        restrictions.organization_restrictions,
        restrictions.registration_restrictions,
        restrictions.membership_restrictions,
        productRestrictions,
        groupRestrictions,
        productMembershipRestrictions
    );
  }

  useEffect(() => {
    setSelectedOption(
      offer.options.find((opt) => opt.subscriber === selectedMember?.value)
    );
  }, [offer, selectedMember?.value]);

  useEffect(() => {
    setItemAddedToCart(null);
  }, [selectedMember?.value]);

  const onMemberSelect = useCallback(
    (member: InputSelectOption<number>) => {
      setSelectedMember(member);
      setSelectedOption(
        offer.options.find((option) => option.subscriber === member.value)
      );
    },
    [offer]
  );

  const addSelectedPlanToCart = useCallback(async () => {
    if (!selectedMember) {
      throw new Error('Cannot add selected plan to cart: no member selected')
    }
    const response = await addSessionToCart(
      selectedMember.value,
      offer.plan.id
    );
    if (!!response && !!response.cart_item) {
      setItemAddedToCart(response.cart_item);
      displaySuccessMessage(t("memberships.successFullyAddedToCart"));
    }
  }, [addSessionToCart, selectedMember, offer]);

  const removeAddedItemFromCart = useCallback(async () => {
    if (!itemAddedToCart) {
      return
    }
    await removeSessionFromCart(itemAddedToCart);
    setItemAddedToCart(null);
  }, [removeSessionFromCart, itemAddedToCart]);

  const hasMultipleGroups = offer.plan.groups.length > 1;
  const isMembership = offer.plan.membership_template_id !== null;

  const { shortFormat, longFormat } = useDateFormats()

  const formattedGroupStartDate = !hasMultipleGroups
    ? moment(
        offer.plan.groups[0] ? offer.plan.groups[0].start_date : Date.now()
      ).format(shortFormat)
    : null;
  const offerDescription = hasMultipleGroups
    ? t("offers.sessionOfferDescription$numberOfGroupsGroups", {
        numberOfGroups: offer.plan.groups.length,
      })
    : t("offers.sessionOfferDescriptionSingleGroup", {
        startDate: formattedGroupStartDate,
      });


  const restrictionFlatten = selectedOption
      ? makeListOfRestrictionCodes(selectedOption.restrictions)
      : [];

  const waitingListAvailable = selectedOption &&
    restrictionFlatten.length === 1 &&
    restrictionFlatten[0] === restrictions.groupIsFull &&
    groupsAllHaveWaitingLists(offer.plan.groups);
  const isAlreadyOnWaitingList = !!selectedOption?.on_waiting_list;
  const canRegisterOnWaitingList =
    waitingListAvailable && !isAlreadyOnWaitingList;

  const isTooSoonToRegister = selectedOption?.start_date
    ? moment(selectedOption.start_date).isAfter(moment())
    : false;
  const isTooLateToRegister = selectedOption?.end_date
    ? moment(selectedOption.end_date).isBefore(moment())
    : false;

  let descriptionComponents = [];
  if (isMembership)
    descriptionComponents.push(
      <PreFormattedParagraph>{t("offers.hasMembership")}</PreFormattedParagraph>
    );
  else descriptionComponents.push(<Paragraph>{offerDescription}</Paragraph>);
  if (canRegisterOnWaitingList)
    descriptionComponents.push(
      <PreFormattedParagraph>{t("offers.canRegisterOnWaitingList")}</PreFormattedParagraph>
    );
  if (!!offer.plan.description)
    descriptionComponents.push(
      <PreFormattedParagraph dangerouslySetInnerHTML={{ __html: getHtmlContent(offer.plan.description) }} />
    );
  if (isTooSoonToRegister)
    descriptionComponents.push(
      <PrecisionParagraph>
        {t("offers.registration$startDate", {
          startDate: moment(selectedOption?.start_date).format(longFormat),
        })}
      </PrecisionParagraph>
    );
  if (!isTooLateToRegister && selectedOption?.end_date)
    descriptionComponents.push(
      <PrecisionParagraph>
        {t("offers.registration$endDate", {
          endDate: moment(selectedOption.end_date).format(longFormat),
        })}
      </PrecisionParagraph>
    );

  let offerComponents = [];
  let price = selectedOption  && selectedOption.price ? selectedOption.price : 0.00
  if (itemAddedToCart) {
    offerComponents.push(
      <RemoveFromCartButton onClick={removeAddedItemFromCart}>
        {t("offers.removeFromCart")}
      </RemoveFromCartButton>
    );
    offerComponents.push(
      <PrimaryLinkButton href={shoppingCartUrl} target="_top">
        {t("offers.consultCart")}
      </PrimaryLinkButton>
    );
  } else if (canRegisterOnWaitingList) {
    offerComponents.push(
      <PriceContainer>
        <Price><MoneyDisplay amount={price} /></Price>
        <PricePrecision>({t("feesAndTaxesIncluded")})</PricePrecision>
      </PriceContainer>
    );
    offerComponents.push(
      <PrimaryButton
        onClick={(hasConsent || hasContact) ? addSelectedPlanToCart : () => setIsModalOpen(true)}
        text={t("offers.registerOnWaitingList")}
      />
    );
  } else if (isAlreadyOnWaitingList) {
    offerComponents.push(
      <Paragraph>{t("offers.alreadyOnWaitingList")}</Paragraph>
    );
  } else if (
    !canRegisterOnWaitingList &&
    selectedOption &&
    restrictionFlatten.length > 0
  ) {
    offerComponents.push(
      <RestrictionMessageContainer>
        <RestrictionMessage
          restrictions={restrictionFlatten}
          multipleGroups={hasMultipleGroups}
          group={hasMultipleGroups ? undefined : offer.plan.groups[0]}
          shouldRenderMembershipOptions={shouldRenderMembershipOptions}
          toggleShoppingButtonDisability={(val) => { }}
        />
      </RestrictionMessageContainer>
    );
  } else if (!isTooSoonToRegister && !isTooLateToRegister) {
    offerComponents.push(
      <PriceContainer>
          <Price><MoneyDisplay amount={price} /></Price>
        <PricePrecision>({t("feesAndTaxesIncluded")})</PricePrecision>
      </PriceContainer>
    );
    offerComponents.push(
      <PrimaryButton
        onClick={(hasConsent || hasContact) ? addSelectedPlanToCart : () => setIsModalOpen(true)}
        text={t("offers.addToCart")}
      />
    );
  }

  // @ts-ignore
  const selectInput = !selectedMemberId && (
    <InputSelect
      value={selectedMember}
      options={membersOptions}
      onChange={onMemberSelect}
      withCaret
    />
  );

  return (
    <SessionOfferContainer>
      {
        !(hasConsent || hasContact)
        && <ConsentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={addSelectedPlanToCart}
          onConsent={onConsent}
          organizationId={organizationId}
          userId={userId}
        />
      }
      <H2>{offer.plan.name}</H2>
      <Column>{descriptionComponents}</Column>
      <MemberSelectAndOfferContainer>
        <MemberSelectContainer>{selectInput}</MemberSelectContainer>
        <OfferContainer>{offerComponents}</OfferContainer>
      </MemberSelectAndOfferContainer>
    </SessionOfferContainer>
  );
};

const SessionOfferContainer = styled(Column)`
  width: 100%;
`;

const MemberSelectAndOfferContainer = styled(Row)`
  width: 100%;
  align-items: center;
  flex-wrap: wrap;
  height: 80px;

  @media only screen and (max-width: 767px) {
    flex-direction: column;
    height: auto;
  }
`;

const MemberSelectContainer = styled.div`
  flex: 1;
  min-width: 250px;
  max-width: 300px;
  padding-right: 100px;
  div {
    margin-bottom: 0;
  }
  @media only screen and (max-width: 767px) {
    margin-bottom: 30px;
    padding-right: 0;
  }
`;

const OfferContainer = styled(Row)`
  flex: 1;
  align-items: flex-end;
  justify-content: flex-end;

  @media only screen and (max-width: 767px) {
    flex-direction: column;
    align-items: center;
  }
`;

const PriceContainer = styled(Row)`
  align-items: flex-end;
  margin-right: 40px;

  @media only screen and (max-width: 767px) {
    margin-right: 0;
    margin-bottom: 30px;
  }
`;
const Price = styled(H1)`
  margin: 0 10px 0 0 !important;
  line-height: 0.9;
`;
const PricePrecision = styled(BoldParagraph)`
  margin: 0;
`;
const RestrictionMessageContainer = styled.div`
  flex: 1;
`;

const RemoveFromCartButton = styled(OutlineButton)`
  margin-right: 20px;
`;

export default CompleteSessionOffer;
